import {
  FilteredGuest,
  SelectionGroup,
  Selections,
  SpaPackageBasketItem,
  AvailabilityForm,
} from '@features/DynamicPackagesConfigurationWizard/types';
import { PackageType } from '@features/DynamicPackagesConfigurationWizard/types/PackageType';
import moment from 'moment';
import { OvernightPackageSpaExtras } from '../types/SpaExtra.schema';
import { MappedLoggedGuestData } from './addToBasket';
import { filterIncompleteGuests, isGuestInFilteredList, parseGuestDetails } from './guest.helpers';

export function mapSpaExtrasToSelectionGroups(
  spaExtras: OvernightPackageSpaExtras,
  filteredGuests: FilteredGuest[]
): SelectionGroup[] {
  const selections = (spaExtras || []).reduce((selections, extra) => {
    extra.selectedGuestDetails.forEach((guest) => {
      if (isGuestInFilteredList(filteredGuests, guest)) {
        const matchingFilteredGuest = filteredGuests.find(
          (g) =>
            g.firstName === guest.firstName &&
            g.lastName === guest.lastName &&
            g.title === guest.title
        );
        selections.push({
          extra: extra.extra,
          guest_details: parseGuestDetails(matchingFilteredGuest),
        });
      }
    });
    return selections;
  }, [] as Selections);
  return [{ is_extras: true, selections }];
}

function mapSpaExperiencesToConfigGroups(
  guestExperiences: (AvailabilityForm | null | undefined)[][],
  filteredGuests: FilteredGuest[]
): SelectionGroup[] {
  const selectionGroups: SelectionGroup[] = [];
  const addedGroupIds = new Set<number | string>();

  const isBookingFilled = (booking: any) =>
    booking &&
    booking.startTime &&
    booking.endTime &&
    booking.experienceId &&
    booking.optionId !== undefined;

  guestExperiences.forEach((guestExp) => {
    (guestExp || []).forEach((booking) => {
      const optionGroupId = booking?.optionGroupId;
      if (optionGroupId && !addedGroupIds.has(optionGroupId)) {
        selectionGroups.push({
          configuration_group: { id: Number(optionGroupId) },
          selections: [],
        });
        addedGroupIds.add(optionGroupId);
      }
    });
  });

  guestExperiences.forEach((guestExp, guestIndex) => {
    const filteredGuest = filteredGuests[guestIndex];
    if (!filteredGuest) return;

    (guestExp || []).forEach((booking) => {
      if (!isBookingFilled(booking) || !booking) return;

      const { optionGroupId, optionId, startTime, endTime, visitDate } = booking;
      const selectionGroup = selectionGroups.find(
        (grp) => Number(grp.configuration_group?.id) === Number(optionGroupId)
      );

      if (!selectionGroup) return;

      selectionGroup.selections.push({
        option: { id: Number(optionId) },
        start_time: startTime,
        end_time: endTime,
        guest_details: parseGuestDetails(filteredGuest),
        start_date: moment(visitDate).format('YYYY-MM-DD'),
        end_date: moment(visitDate).format('YYYY-MM-DD'),
      });
    });
  });

  return selectionGroups;
}

function mapRepeatableSpaExperiencesToConfigGroupsByNight(
  guestExperiences: (AvailabilityForm | null | undefined)[][],
  filteredGuests: FilteredGuest[]
): SelectionGroup[][] {
  const uniqueDates = new Set<string>();

  guestExperiences.forEach((guestExp) => {
    (guestExp || []).forEach((booking) => {
      if (booking?.visitDate) {
        uniqueDates.add(moment(booking.visitDate).format('YYYY-MM-DD'));
      }
    });
  });

  const nightlyGroups: SelectionGroup[][] = [];

  uniqueDates.forEach((dateKey) => {
    const experiencesForDate = guestExperiences.map((guestExp) =>
      (guestExp || []).filter((booking) => {
        if (!booking?.visitDate) return false;
        return moment(booking.visitDate).format('YYYY-MM-DD') === dateKey;
      })
    );

    const selectionGroups = mapSpaExperiencesToConfigGroups(experiencesForDate, filteredGuests);
    nightlyGroups.push(selectionGroups);
  });

  return nightlyGroups;
}

function mapRepeatableSpaExperiencesByRepetition(
  guestExperiences: (AvailabilityForm | null | undefined)[][],
  filteredGuests: FilteredGuest[],
  numberOfNights: number
): SelectionGroup[][] {
  if (numberOfNights <= 0) return [];

  const slotsForGuestGroup = (guestExp: (AvailabilityForm | null | undefined)[]) => {
    const byGroup = new Map<number, AvailabilityForm[]>();
    guestExp.forEach((booking) => {
      if (!booking?.optionGroupId) return;
      const id = Number(booking.optionGroupId);
      if (!byGroup.has(id)) byGroup.set(id, []);
      byGroup.get(id)!.push(booking);
    });
    return byGroup;
  };

  const repetitions: (AvailabilityForm | null | undefined)[][][] = Array.from(
    { length: numberOfNights },
    () => guestExperiences.map(() => [])
  );

  guestExperiences.forEach((guestExp, guestIndex) => {
    const byGroup = slotsForGuestGroup(guestExp || []);
    byGroup.forEach((bookings) => {
      const perRepetition = Math.max(1, Math.floor(bookings.length / numberOfNights));
      bookings.forEach((booking, idx) => {
        const repetitionIndex = Math.min(numberOfNights - 1, Math.floor(idx / perRepetition));
        repetitions[repetitionIndex][guestIndex].push(booking);
      });
    });
  });

  return repetitions
    .map((repetitionGuestExp) =>
      mapSpaExperiencesToConfigGroups(repetitionGuestExp, filteredGuests)
    )
    .filter((groups) => groups.some((g) => g.selections.length > 0));
}

export function parseSpaExperiencesForBasket(
  guestDetails: any[],
  guestExperiences: (AvailabilityForm | null | undefined)[][],
  spaExtras: OvernightPackageSpaExtras,
  spaPackageId: number | string,
  fromDate: string,
  toDate: string,
  arrivalTime: string,
  endTime: string,
  maxGuestsPerExperience: number,
  loggedInUserGuestData: MappedLoggedGuestData | null,
  packageType: PackageType,
  parentBasketId?: number,
  isRepeatedFlexibleEnabled = false,
  stayDuration?: number
): SpaPackageBasketItem[] {
  const filteredGuests = filterIncompleteGuests(guestDetails, loggedInUserGuestData);
  const dateDiff = moment(toDate).diff(moment(fromDate), 'days');
  if (dateDiff < 0) {
    throw new Error(
      `parseSpaExperiencesForBasket: toDate (${toDate}) is before fromDate (${fromDate})`
    );
  }
  if (isRepeatedFlexibleEnabled && packageType === PackageType.REPEATING && !stayDuration) {
    throw new Error(
      'parseSpaExperiencesForBasket: stayDuration is required for REPEATING packages when feature.package-repeated-flexible is enabled'
    );
  }
  const numberOfNights = stayDuration && stayDuration > 0 ? stayDuration : Math.max(1, dateDiff);

  const groupRepeating = (
    exp: (AvailabilityForm | null | undefined)[][],
    guests: FilteredGuest[]
  ) =>
    isRepeatedFlexibleEnabled
      ? mapRepeatableSpaExperiencesByRepetition(exp, guests, numberOfNights)
      : mapRepeatableSpaExperiencesToConfigGroupsByNight(exp, guests);

  const earliestSelectionDate = (grp: SelectionGroup[]): string | undefined => {
    let earliest: string | undefined;
    grp.forEach((group) => {
      group.selections.forEach((sel) => {
        if ('start_date' in sel && sel.start_date) {
          if (!earliest || sel.start_date < earliest) earliest = sel.start_date;
        }
      });
    });
    return earliest;
  };

  if (filteredGuests.length > maxGuestsPerExperience) {
    // Create separate basket items for each guest
    return filteredGuests
      .map((guest, index) => {
        const currentGuestExperiences = guestExperiences[index] || [];

        const mappedSelectionGroups =
          packageType === PackageType.REPEATING
            ? groupRepeating([currentGuestExperiences], [guest])
            : [mapSpaExperiencesToConfigGroups([currentGuestExperiences], [guest])];

        const mappedExtraSelectionGroups = mapSpaExtrasToSelectionGroups(spaExtras, [guest]);
        return mappedSelectionGroups
          .map((grp) => {
            const firstSelection = grp[0]?.selections?.[0];
            const fallbackDate =
              firstSelection && 'start_date' in firstSelection && firstSelection.start_date
                ? firstSelection.start_date
                : moment(fromDate).format('YYYY-MM-DD');
            const visitDateFromSelection = isRepeatedFlexibleEnabled
              ? earliestSelectionDate(grp) ?? fallbackDate
              : fallbackDate;

            const isRepeatableAndAllOnSameDay =
              packageType === PackageType.REPEATING &&
              grp.every((group) =>
                group.selections.every(
                  (sel) => 'start_date' in sel && sel.start_date === visitDateFromSelection
                )
              );

            const checkOutDate = isRepeatableAndAllOnSameDay
              ? visitDateFromSelection
              : packageType === PackageType.REPEATING
              ? moment(visitDateFromSelection).add(1, 'day').format('YYYY-MM-DD')
              : moment(toDate).format('YYYY-MM-DD');

            return {
              type: 'spa-packages',
              spa_package: {
                id: Number(spaPackageId),
              },
              check_in: `${visitDateFromSelection}  ${arrivalTime}`,
              check_out: `${checkOutDate}  ${endTime}`,
              guest_details: [parseGuestDetails(guest)],
              dynamic_package: 'child',
              package_basket_item_id: parentBasketId,
              selection_groups: [...grp, ...mappedExtraSelectionGroups],
            };
          })
          .flat();
      })
      .flat();
  } else {
    // Create single basket item for all guests
    const mappedSelectionGroups =
      packageType === PackageType.REPEATING
        ? groupRepeating(guestExperiences, filteredGuests)
        : [mapSpaExperiencesToConfigGroups(guestExperiences, filteredGuests)];
    const mappedExtraSelectionGroups = mapSpaExtrasToSelectionGroups(spaExtras, filteredGuests);

    return mappedSelectionGroups
      .map((grp) => {
        const firstSelection = grp[0]?.selections?.[0];
        const fallbackDate =
          firstSelection && 'start_date' in firstSelection && firstSelection.start_date
            ? firstSelection.start_date
            : moment(fromDate).format('YYYY-MM-DD');
        const visitDateFromSelection = isRepeatedFlexibleEnabled
          ? earliestSelectionDate(grp) ?? fallbackDate
          : fallbackDate;

        const hasMultipleDays = grp.some((group) =>
          group.selections.some(
            (sel) => 'start_date' in sel && sel.start_date !== visitDateFromSelection
          )
        );
        const isRepeatableAndAllOnSameDay =
          packageType === PackageType.REPEATING && !hasMultipleDays;

        const checkOutDate = isRepeatableAndAllOnSameDay
          ? visitDateFromSelection
          : packageType === PackageType.REPEATING
          ? moment(visitDateFromSelection).add(1, 'day').format('YYYY-MM-DD')
          : moment(toDate).format('YYYY-MM-DD');

        return {
          type: 'spa-packages',
          spa_package: {
            id: Number(spaPackageId),
          },
          check_in: `${visitDateFromSelection}  ${arrivalTime}`,
          check_out: `${checkOutDate}  ${endTime}`,
          guest_details: filteredGuests.map(parseGuestDetails),
          dynamic_package: 'child',
          ...(parentBasketId ? { package_basket_item_id: parentBasketId } : {}),
          selection_groups: [...grp, ...mappedExtraSelectionGroups],
        };
      })
      .flat();
  }
}
